import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { Box, Link, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { AI_ASSISTED_SESSION_DISPLAY, FeeRecommendationSource } from 'constants/feeRecommendations';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { getSessionStatusDisplay } from 'utils/feeRecommendations';

import { openDialogAction } from 'store/global/actions';
import {
  acceptAllLoanFeeRecommendationsAction,
  acceptLoanFeeRecommendationItemAction,
  getLoanFeeRecommendationsAction,
  rejectAllLoanFeeRecommendationsAction,
  rejectLoanFeeRecommendationItemAction,
} from 'store/loanApplications/actions';
import { selectLoanFeeRecommendations } from 'store/loanApplications/selectors';

import { LoanFeesTestIds } from '../../LoanFees.testIds';

import FeeRecommendationItem from './components/FeeRecommendationItem';
import RejectFeeRecommendationModal from './components/RejectFeeRecommendationModal';
import SessionReviewActions from './components/SessionReviewActions';
import { REJECT_ALL_DESCRIPTION, ReviewAction } from './FeeRecommendationsPanel.constants';
import {
  headerStyles,
  rootStyles,
  sessionHeaderStyles,
  sessionStyles,
} from './FeeRecommendationsPanel.styles';
import {
  getPendingItemIds,
  getReviewPendingKey,
  getSourceDocumentPath,
} from './FeeRecommendationsPanel.utils';

const FeeRecommendationsPanel = ({ loanId, onReviewed }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const recommendations = useSelector(selectLoanFeeRecommendations);

  const [pendingKey, setPendingKey] = useState(null);

  const isReviewPending = !_.isNull(pendingKey);

  const refetchRecommendations = useCallback(
    () => dispatch(getLoanFeeRecommendationsAction({ loanId, silent: true })).catch(Error),
    [dispatch, loanId],
  );

  const refetchOnConflict = useCallback(
    error => {
      if (error?.response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT) {
        refetchRecommendations();
      }

      return Promise.reject(error);
    },
    [refetchRecommendations],
  );

  // Only the recommendations refresh gates the review controls: onReviewed reloads the whole Fees
  // tab, which can take 20s+ on slow fee endpoints and must not hold the reject modal open.
  const runReview = useCallback(
    (key, review) => {
      setPendingKey(key);

      return review()
        .then(() => {
          onReviewed();

          return refetchRecommendations();
        })
        .catch(refetchOnConflict)
        .finally(() => setPendingKey(null));
    },
    [onReviewed, refetchOnConflict, refetchRecommendations],
  );

  const openRejectModal = useCallback(
    ({ description, onReject }) => {
      dispatch(
        openDialogAction({
          header: 'Reject Fee Recommendation',
          size: ModalSize.SMALL,
          isRestyled: true,
          body: dialogProps => (
            <RejectFeeRecommendationModal
              description={description}
              onReject={onReject}
              {...dialogProps}
            />
          ),
        }),
      );
    },
    [dispatch],
  );

  const onAcceptItem = useCallback(
    itemId =>
      runReview(getReviewPendingKey(ReviewAction.ACCEPT, itemId), () =>
        dispatch(acceptLoanFeeRecommendationItemAction({ loanId, itemId })),
      ).catch(Error),
    [dispatch, loanId, runReview],
  );

  const onRejectItem = useCallback(
    (itemId, feeName) =>
      openRejectModal({
        description: feeName,
        onReject: reviewNotes =>
          runReview(getReviewPendingKey(ReviewAction.REJECT, itemId), () =>
            dispatch(rejectLoanFeeRecommendationItemAction({ loanId, itemId, reviewNotes })),
          ),
      }),
    [dispatch, loanId, openRejectModal, runReview],
  );

  const onAcceptAll = useCallback(
    (sessionId, itemIds) =>
      runReview(getReviewPendingKey(ReviewAction.ACCEPT, sessionId), () =>
        dispatch(acceptAllLoanFeeRecommendationsAction({ loanId, sessionId, itemIds })),
      ).catch(Error),
    [dispatch, loanId, runReview],
  );

  const onRejectAll = useCallback(
    sessionId =>
      openRejectModal({
        description: REJECT_ALL_DESCRIPTION,
        onReject: reviewNotes =>
          runReview(getReviewPendingKey(ReviewAction.REJECT, sessionId), () =>
            dispatch(rejectAllLoanFeeRecommendationsAction({ loanId, sessionId, reviewNotes })),
          ),
      }),
    [dispatch, loanId, openRejectModal, runReview],
  );

  if (!recommendations || _.isEmpty(recommendations.sessions)) {
    return null;
  }

  return (
    <Box data-testid={LoanFeesTestIds.FEE_RECOMMENDATIONS_PANEL} sx={rootStyles}>
      <Box sx={headerStyles}>
        <Typography variant="bold16">Fee Recommendations from Title Agent</Typography>
        {recommendations.totalPending > 0 && (
          <LabelCDS
            size={LabelSize.M}
            title={`${recommendations.totalPending} pending`}
            variant={LabelVariant.ORANGE}
          />
        )}
      </Box>

      {recommendations.sessions.map(session => {
        const sessionStatus = getSessionStatusDisplay(session);
        const pendingItemIds = getPendingItemIds(session);

        return (
          <Box key={session.id} sx={sessionStyles}>
            <Box sx={sessionHeaderStyles}>
              <Box sx={headerStyles}>
                <Typography variant="medium14">
                  {dayjs(session.createdDate).format(COMMON_DATETIME_FORMAT)}
                </Typography>
                {sessionStatus && (
                  <LabelCDS
                    size={LabelSize.S}
                    title={sessionStatus.label}
                    variant={sessionStatus.variant}
                  />
                )}
                {session.source === FeeRecommendationSource.AI_PROCESS && (
                  <LabelCDS
                    size={LabelSize.S}
                    title={AI_ASSISTED_SESSION_DISPLAY.label}
                    variant={AI_ASSISTED_SESSION_DISPLAY.variant}
                  />
                )}
                {session.source === FeeRecommendationSource.AI_PROCESS &&
                  session.sourceDocumentId && (
                    <Link
                      component={RouterLink}
                      data-testid={LoanFeesTestIds.getFeeRecommendationSourceDocumentId(session.id)}
                      to={getSourceDocumentPath(applicationId, session.sourceDocumentId)}
                      variant="regular14"
                    >
                      Source document
                    </Link>
                  )}
              </Box>

              {pendingItemIds.length > 0 && (
                <SessionReviewActions
                  isAccepting={pendingKey === getReviewPendingKey(ReviewAction.ACCEPT, session.id)}
                  isDisabled={isReviewPending}
                  pendingItemIds={pendingItemIds}
                  sessionId={session.id}
                  onAcceptAll={onAcceptAll}
                  onRejectAll={onRejectAll}
                />
              )}
            </Box>

            {session.items.map(item => (
              <FeeRecommendationItem
                isAccepting={pendingKey === getReviewPendingKey(ReviewAction.ACCEPT, item.id)}
                isDisabled={isReviewPending}
                item={item}
                key={item.id}
                onAccept={onAcceptItem}
                onReject={onRejectItem}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

FeeRecommendationsPanel.propTypes = {
  loanId: PropTypes.string.isRequired,
  onReviewed: PropTypes.func.isRequired,
};

export default memo(FeeRecommendationsPanel);
